import { toast } from 'react-toastify';

import { callApi } from '../../../../apiCaller';
import { toastError } from '../../../../utils';

import { setIsLoading, setIsNoticeLoading } from '../../../../actions/general';
import { API_SUBSCRIBER_USER_LESSON, API_SUBSCRIBER_USER_LESSON_DETAIL, COURSE_STATUS, TRAINING_PRODUCT_TYPES } from '../../../../constants';

import { API_SUBSCRIBER_COURSE_DETAIL, setCourses, updateCourse } from './Course/actions';
import { setUserLesson, setUserLessons } from '../GlobalStates/UserLesson/actions';
import { setNotifications } from '../GlobalStates/Notification/actions';

export const API_SUBSCRIBER_NOTIFICATIONS = 'notification';
export const API_SUBSCRIBER_CERTIFICATE = 'user/certificate';
export const API_SUBSCRIBER_GAIN_ACCESS = 'user/product/gain-access/:productId';
export const API_SUBSCRIBER_CREATE_COURSE_SYSTEM = 'user/product/create-course-system/:productId';

export const generateCertificatePdf = (courseId, ceCertificate, setCertificatePdf) => (dispatch, getState) => {
     const courses = getState().subscriber.courses;
     const course = courses?.find((item) => item.id === courseId);
     let updateBy = 'lessonStatus';

     if (course) {
          if (ceCertificate && course.cePackStatus === COURSE_STATUS.COMPLETED) {
               updateBy = 'cePackStatus';
          } else if (
               course.event &&
               course.confirmAttended &&
               course.testStatus === COURSE_STATUS.NOT_STARTED &&
               course.surveyStatus === COURSE_STATUS.NOT_STARTED
          ) {
               updateBy = 'confirmAttended';
          } else if (course.surveyStatus === COURSE_STATUS.COMPLETED) {
               updateBy = 'surveyStatus';
          } else if (course.testStatus === COURSE_STATUS.COMPLETED) {
               updateBy = 'testStatus';
          }
     }

     dispatch(setIsLoading(true));

     callApi(`${API_SUBSCRIBER_CERTIFICATE}`, 'POST', { courseId, updateBy })
          .then((response) => {
               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               setCertificatePdf(response.data);
          })
          .finally(() => {
               dispatch(setIsLoading(false));
          });
};

export const fetchUserLessonsSubscriber = (seatId) => (dispatch) => {
     callApi(`${API_SUBSCRIBER_USER_LESSON}?seatId=${seatId}`, 'GET').then((response) => {
          if (response.status === 200) {
               dispatch(setUserLessons(response.data));
          } else {
               toastError(response);
          }
     });
};

export const createUserLesson = (lessonId, seatId) => (dispatch, getState) => {
     const userLessons = getState().subscriber.userLessons;

     callApi(API_SUBSCRIBER_USER_LESSON_DETAIL.replace(':id', lessonId), 'POST', { seatId }).then((response) => {
          if (response.status !== 200) {
               toastError(response);
               return;
          }

          const newUserLessons = [...userLessons, response.data];

          dispatch(setUserLesson(response.data));
          dispatch(setUserLessons(newUserLessons));
     });
};

export const updateGainAccess = (product, fetchCoursesData, setShowPopup, setSubmitting) => (dispatch, getState) => {
     const courses = getState().subscriber.courses;

     if (setSubmitting) {
          setSubmitting(true);
     }

     callApi(API_SUBSCRIBER_GAIN_ACCESS.replace(':productId', product.id), 'POST').then((response) => {
          if (response.status !== 200) {
               toastError(response);
               return;
          }

          const newCourse = response.data;
          const newCourses = [];
          const courseExists = courses.some((course) => course.id === newCourse.id);

          if (courseExists) {
               newCourses.push(
                    ...courses.map((course) => {
                         if (course.id === newCourse.id) {
                              course = { ...course, ...newCourse };
                         }

                         return course;
                    })
               );
          } else {
               newCourses.push(...courses, { ...newCourse });
          }

          dispatch(setCourses(newCourses));

          if (fetchCoursesData) {
               fetchCoursesData(newCourses);
          }

          setShowPopup(false);
          toast.success('Update successfully');

          if (setSubmitting) {
               setSubmitting(false);
          }
     });
};

export const createCourseSystemRequest = (product) => async (dispatch, getState) => {
     const courses = getState().subscriber.courses;

     return await callApi(API_SUBSCRIBER_CREATE_COURSE_SYSTEM.replace(':productId', product.id), 'POST').then((response) => {
          if (response.status !== 200) {
               toastError(response);
               return;
          }

          const newCourse = response.data;
          if (!newCourse.id) return;

          const newCourses = [];
          const courseExists = courses.some((course) => course.id === newCourse.id);

          if (courseExists) {
               newCourses.push(
                    ...courses.map((course) => {
                         if (course.id === newCourse.id) {
                              course = { ...course, ...newCourse };
                         }

                         return course;
                    })
               );
          } else {
               newCourses.push(...courses, { ...newCourse });
          }

          return newCourses;
     });
};

export const fetchNotificationsRequest =
     ({ page, pageSize }) =>
     async (dispatch) => {
          dispatch(setIsNoticeLoading(true));

          return await callApi(`${API_SUBSCRIBER_NOTIFICATIONS}?page=${page}&pageSize=${pageSize}`, 'GET').then((response) => {
               if (response.status !== 200) {
                    dispatch(setIsNoticeLoading(false));
                    toastError(response);
                    return;
               }

               dispatch(setNotifications(response.data));
               dispatch(setIsNoticeLoading(false));
               return response.data;
          });
     };

export const fetchSubscriberCourseDetailsRequest = (courseId) => (dispatch) => {
     callApi(API_SUBSCRIBER_COURSE_DETAIL.replace(':courseId', courseId), 'GET').then((response) => {
          if (response.status !== 200) {
               toastError(response);
               return;
          }

          dispatch(updateCourse(response.data));

          if (response.data.status === COURSE_STATUS.COMPLETED) {
               dispatch(fetchCourseDetailsPrerequisitesRequest(courseId));
          }
     });
};

export const fetchCourseDetailsPrerequisitesRequest = (courseId) => (dispatch, getState) => {
     const courses = getState().subscriber.courses;
     const products = getState().subscriber.products.list;

     const courseIds = [];
     const productId = courses?.find((course) => course.id === courseId)?.productId;
     const productPrerequisites = products?.find((prod) => prod.productPrerequisites.length)?.productPrerequisites;
     const productIds =
          productPrerequisites?.length && productId
               ? productPrerequisites.filter((prodPre) => prodPre.prerequisiteId === productId).map((prodPre) => prodPre.productId)
               : [];

     if (products && courses) {
          for (let i = 0; i < productIds.length; i++) {
               const productId = productIds[i];
               const product = products.find((prod) => prod.id === productId);
               const trainingProductType = product?.productType.trainingProductType;
               const courseId = courses.find((course) => course.productId === productId)?.id;

               if (courseId && trainingProductType === TRAINING_PRODUCT_TYPES.CE_CREDITS) {
                    courseIds.push(courseId);
               }
          }
     }

     for (let i = 0; i < courseIds.length; i++) {
          const courseId = courseIds[i];

          callApi(API_SUBSCRIBER_COURSE_DETAIL.replace(':courseId', courseId), 'GET').then((response) => {
               if (response.status === 200) {
                    dispatch(updateCourse(response.data));
               } else {
                    toastError(response);
               }
          });
     }
};
